/**
 * fingerprint — Assist Form Contract v1 (12/09/2026)
 *
 * ÚNICA implementação de canonicalização + hash da camada de contrato.
 * O DOMÍNIO decide o que é "estado relevante" (getRevisionSource); esta
 * função decide como isso vira uma revisão estável. Nenhum adapter deve
 * implementar seu próprio JSON.stringify/hash.
 *
 * Canonicalização: chaves de objeto ordenadas; `undefined` e funções
 * descartados; arrays preservam ordem (ordem É estado em séries/blocos);
 * NaN → null; -0 → 0. Determinístico entre execuções.
 *
 * Hash: FNV-1a 32 bits × 2 sementes (síncrono, sem dependência). É um
 * DETECTOR DE CONFLITO DE UI — não é assinatura, não é autorização e não
 * substitui controle otimista (ETag/rowversion) no banco.
 */

export function canonicalize(value) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value === null) return null;
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return null;
    return Object.is(value, -0) ? 0 : value;
  }
  if (typeof value !== 'object') return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((v) => { const c = canonicalize(v); return c === undefined ? null : c; });
  const out = {};
  for (const k of Object.keys(value).sort()) {
    const c = canonicalize(value[k]);
    if (c !== undefined) out[k] = c;
  }
  return out;
}

export function canonicalJson(value) {
  const c = canonicalize(value);
  return JSON.stringify(c === undefined ? null : c);
}

function fnv1a(str, seed) {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** @returns {string} 'fp:' + 16 hex */
export function fingerprint(revisionSource) {
  const json = canonicalJson(revisionSource);
  return `fp:${fnv1a(json, 0x811c9dc5)}${fnv1a(json, 0x9747b28c)}`;
}
